import React, { useState, useRef, useEffect } from 'react';
import { Menu } from 'lucide-react';
import { spacing, components } from '../utils/designTokens';
import { LOADING_MESSAGE, EMPTY_DATA_MESSAGE } from '../utils/strings';
import PageHeader from './PageHeader';
import Breadcrumb from './Breadcrumb';
import Pagination from './Pagination';
import ErrorState from './ErrorState';

/**
 * TabbedPageLayout - Layout wrapper for pages that may have tabs
 *
 * Features:
 * - Optional breadcrumb navigation with referrerUrl support
 * - Page header with title and display label
 * - Optional tabs below header
 * - Optional hamburger menu (to the right of the tabs) for secondary views
 * - Stats bar with entity counts
 * - KPI pills section
 * - Filter pills section
 * - Main content area
 * - New Pagination component (First/Previous/Next/Last)
 * - Centralized loading/empty state handling
 *
 * Note: GlobalSelectors (Environment, App, TimeWindow) are now in the main Header component.
 *
 * Used by:
 * - Home (no tabs)
 * - AppOverview (with tabs)
 */
const TabbedPageLayout = ({
  // Navigation
  breadcrumbItems,   // Optional: Array of { label, to } for breadcrumb navigation

  // Header
  title,             // Page title
  titleValue,        // Optional: Value to display after title in muted color (e.g., app name)
  displayLabel,      // Optional: Display label (e.g., "dev • Last 24h")

  // Tabs
  tabs = [],         // Optional: Array of tab objects
  activeTab,         // Currently active tab
  onTabChange,       // Tab change handler

  // Secondary views, tucked behind a hamburger menu to the right of the tabs
  menuItems = [],    // Optional: Array of { key, label } — clicking calls onTabChange(key)

  // Sections
  stats,             // <StatsBar> component with entity counts
  kpis,              // <KPIBar> component with KPI pills
  filters,           // <FilterPills> or filter components

  // Data & State
  loading,           // Boolean: true when fetching data
  error,             // String or null: error message to display
  info,              // String or null: info message for boundary conditions (e.g., no data in time window)
  data,              // Array or null: null = not fetched yet, [] = fetched but empty, [...] = has data
  showEmptyState = true, // Boolean: if false, children handle their own empty states (for chart pages)

  // Pagination (uses new Pagination component)
  currentPage,       // Current page number (1-indexed)
  itemsPerPage,      // Items per page
  totalItems,        // Total number of items
  onPageChange,      // Page change handler (receives new page number)

  children           // Main content (only rendered when data exists)
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  // Validate required props
  if (!title) throw new Error('TabbedPageLayout requires title prop');
  if (tabs.length > 0 && !activeTab) throw new Error('TabbedPageLayout with tabs requires activeTab prop');
  if (tabs.length > 0 && !onTabChange) throw new Error('TabbedPageLayout with tabs requires onTabChange prop');
  if (menuItems.length > 0 && !onTabChange) throw new Error('TabbedPageLayout with menuItems requires onTabChange prop');

  const isMenuItemActive = menuItems.some((item) => (item.key || item.label) === activeTab);
  const leftTabs = tabs.filter((tab) => tab.align !== 'end');
  const rightTabs = tabs.filter((tab) => tab.align === 'end');

  return (
    <div className={`flex flex-col h-full ${spacing.md}`}>
      {/* Breadcrumb section (optional, above header) */}
      {breadcrumbItems && breadcrumbItems.length > 0 && (
        <div className="flex-shrink-0">
          <Breadcrumb items={breadcrumbItems} />
        </div>
      )}

      {/* Header section */}
      <div className="flex-shrink-0 flex items-center gap-6">
        {/* Title + display label */}
        <div className="flex items-end gap-3">
          <h1 className="text-xl font-bold">
            <span className="text-text-primary">{title}</span>{titleValue && <span className="text-text-muted"> {titleValue}</span>}
          </h1>
          {displayLabel && (
            <span className="text-[11px] text-text-neutral">
              {displayLabel}
            </span>
          )}
        </div>

        {/* Tabs inline with title - pill style with periwinkle fill */}
        {leftTabs.length > 0 && (
          <div className="flex gap-2">
            {leftTabs.map((tab) => {
              const isActive = (tab.key || tab.label) === activeTab;
              return (
                <button
                  key={tab.key || tab.label}
                  onClick={() => onTabChange(tab.key || tab.label)}
                  className={`px-2.5 py-1 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-accent-primary-base text-text-inverse'
                      : 'bg-background-tertiary text-text-supporting hover:bg-background-quaternary'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        )}

        {/* Right-aligned tabs + hamburger menu (e.g. Signals next to RCA/Simulation Studio) */}
        {(rightTabs.length > 0 || menuItems.length > 0) && (
          <div className="flex items-center gap-2 ml-auto">
            {rightTabs.map((tab) => {
              const isActive = (tab.key || tab.label) === activeTab;
              return (
                <button
                  key={tab.key || tab.label}
                  onClick={() => onTabChange(tab.key || tab.label)}
                  className={`px-2.5 py-1 rounded-full text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-accent-primary-base text-text-inverse'
                      : 'bg-background-tertiary text-text-supporting hover:bg-background-quaternary'
                  }`}
                >
                  {tab.label}
                </button>
              );
            })}

            {menuItems.length > 0 && (
              <div className="relative" ref={menuRef}>
                <button
                  onClick={() => setIsMenuOpen((open) => !open)}
                  className={`flex items-center justify-center w-8 h-8 rounded-full transition-colors ${
                    isMenuItemActive
                      ? 'bg-accent-primary-base text-text-inverse'
                      : 'bg-background-tertiary text-text-supporting hover:bg-background-quaternary'
                  }`}
                  aria-label="More views"
                  title="More views"
                >
                  <Menu size={16} />
                </button>

                {isMenuOpen && (
                  <div className="absolute top-full right-0 mt-1 shadow-lg z-50 min-w-[180px] bg-background-secondary border border-border-subtle rounded-lg overflow-hidden">
                    {menuItems.map((item) => {
                      const key = item.key || item.label;
                      const isActive = key === activeTab;
                      return (
                        <button
                          key={key}
                          onClick={() => { onTabChange(key); setIsMenuOpen(false); }}
                          className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                            isActive
                              ? 'bg-accent-primary-base text-text-inverse'
                              : 'text-text-primary hover:bg-background-tertiary'
                          }`}
                        >
                          {item.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* KPI, Filter and Stats sections - hidden during loading */}
      {!loading && (stats || kpis || filters) && (
        <div className="flex-shrink-0 flex items-center justify-between gap-4">
          {/* Actionable filters group */}
          <div className="flex items-center gap-2">
            {kpis && <div className="flex-1">{kpis}</div>}
            {filters && <div className="flex-shrink-0">{filters}</div>}
          </div>

          {/* Informational metadata */}
          {stats && <div className="flex-shrink-0">{stats}</div>}
        </div>
      )}

      {/* Main content area with centralized loading/error/info/empty state */}
      {/* Children are always rendered to preserve internal state (e.g., sub-tab selection) */}
      <div className="flex-1 min-h-0">
        {/* State overlays - shown when applicable */}
        {loading && (
          <div className={components.table.empty}>
            {LOADING_MESSAGE}
          </div>
        )}
        {!loading && error && (
          <div className="p-6">
            <ErrorState
              title="Error"
              message={error}
            />
          </div>
        )}
        {!loading && !error && info && (
          <div className={components.table.empty}>
            <div className="text-text-muted">{info}</div>
            <div className="text-text-muted text-sm mt-2">Try selecting a longer time range</div>
          </div>
        )}
        {showEmptyState && !loading && !error && !info && data?.length === 0 && (
          <div className={components.table.empty}>
            {EMPTY_DATA_MESSAGE}
          </div>
        )}
        {/* Children - always rendered (preserves state), hidden when showing overlays */}
        <div className={loading || error || info || (showEmptyState && data?.length === 0) ? 'hidden' : 'h-full'}>
          {children}
        </div>
      </div>

      {/* Pagination controls - using new Pagination component */}
      {!loading && data?.length > 0 && currentPage != null && itemsPerPage != null && totalItems != null && (
        <div className="flex-shrink-0">
          <Pagination
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onPageChange={onPageChange}
            loading={loading}
          />
        </div>
      )}
    </div>
  );
};

export default TabbedPageLayout;
